import styles from "./styles.module.scss";
import { useEffect, useRef, useState } from "react";
import { CardInfo } from "../components/CardInfo";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../../themeGrid";
import { localeText } from "../../../localeTextFromGrid";
import { ColDef, IGetRowsParams } from "ag-grid-community";
import { IApiResponse } from "../../../@types/api-response.type";
import { getProjectImplementedByProjectIdAndMonthYear, searchProjectImplementedByProjectId } from "../../../services/api/ProjectImplemented/ProjectImplementedService";
import { IProjectImplementedFilter, IProjectImplementedsResponse } from "../../../@types/project/project-implemented.type";
import { LineChartImplemented } from "../components/LineChartImplemented";
import { BarChartImplemented } from "../components/BarChartImplemented";
import { formatCurrency } from "../../../utils/formatCurrency";
import Button from "@mui/material/Button";

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import vectorIcon from "../../../assets/_VectorIcon_.svg";
import dayjs from "dayjs";
import { calculateTimeRange } from "../../../utils/timeUtils";
import { DefaultFilterImplemented } from "./DefaultFilter";


let columnsDefault: ColDef[] = [
  {
    field: "id",
    headerName: "ID",
    hide: true,
  },
  { field: "name", headerName: "Período", flex: 1, minWidth: 150 },
  { field: "positionName", headerName: "Função", flex: 1, minWidth: 150, hide: false },
  { field: "budgetHours", headerName: "Contratado (h)", flex: 1, minWidth: 125,
    cellRenderer: (params: CustomCellRendererProps) => {
      return <>{params.value ? params.value + " h" : "0 h" }</>
    },
  },
  { field: "plannedHours", headerName: "Planejado (h)", flex: 1, minWidth: 125,
    cellRenderer: (params: CustomCellRendererProps) => {
      return <>{params.value ? params.value + " h" : "0 h" }</>
    },
  },
  { field: "implementedHours", headerName: "Alocado (h)", flex: 1, minWidth: 125,
    cellRenderer: (params: CustomCellRendererProps) => {
      return <>{params.value ? params.value + " h" : "0 h" }</>
    },
  },
  { field: "hoursPercentage", headerName: "%", flex: 1, minWidth: 50,
    cellRenderer: (params: CustomCellRendererProps) => {
      return <>{params.value ? Math.round(params.value) + " %" : "0 %" }</>
    },
  },
  { field: "budgetPrice", headerName: "Contratado (R$)", flex: 1, minWidth: 150,
    cellRenderer: (params: CustomCellRendererProps) => {
      return params.value ? formatCurrency(params.value) : "-";
    },
  },
  { field: "plannedPrice", headerName: "Planejado (R$)", flex: 1, minWidth: 150,
    cellRenderer: (params: CustomCellRendererProps) => {
      return params.value ? formatCurrency(params.value) : "-";
    },
  },
  { field: "implementedPrice", headerName: "Alocado (R$)", flex: 1, minWidth: 150,
    cellRenderer: (params: CustomCellRendererProps) => {
      return params.value ? formatCurrency(params.value) : "-";
    },
  },
  { field: "pricePercentage", headerName: "%", flex: 1, minWidth: 50,
    cellRenderer: (params: CustomCellRendererProps) => {
      return <>{params.value ? Math.round(params.value) + " %" : "0 %" }</>
    },
  },
];


interface ImplementedProjectProps {
  projectId: string,
}

export const ImplementedProject: React.FC<ImplementedProjectProps> = ({projectId}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const gridRef = useRef<AgGridReact<any>>(null);
  const [columns, setColumns] = useState(columnsDefault);
  const [obj, setObj] = useState<IProjectImplementedsResponse>();
  const [isDetails, setIsDetails] = useState<boolean>(false);
  const [graphType, setGraphType] = useState<number>(0); // 0 = custo, 1 = hora

  const [filterState, setFilterState] = useState({
    timeMode: 1,
    period: "Anual",
    //period: "Mensal",
    currentPeriod: dayjs(),
  });

  const handleFilterChange = (
    timeMode: number,
    period: string,
    currentPeriod: dayjs.Dayjs
  ) => {
    setFilterState({ timeMode, period, currentPeriod });
  };

  useEffect(() => {
    loadInfo();
  }, [filterState]);

  useEffect(() => {
    let index = columnsDefault.findIndex(x => x.field == "name");
    let position = columnsDefault.findIndex(x => x.field == "positionName");
    if(isDetails){
      columnsDefault[index].headerName = "Perfil";
      columnsDefault[position].hide = false;
    } else{
      columnsDefault[index].headerName = "Período";
      columnsDefault[position].hide = true;
    }
    if(gridRef.current!.api)
      gridRef.current!.api.setGridOption("columnDefs", columnsDefault)
  }, [isDetails]);

  const loadInfo = async () =>{
    const { startDate, endDate } = calculateTimeRange(
      filterState.currentPeriod,
      filterState.period
    );
    const filter = {
      startDate: filterState.period === "Total" ? null : startDate.startOf("day").toISOString(),
      endDate: filterState.period === "Total" ? null : endDate.startOf("day").toISOString(),
    } as IProjectImplementedFilter;
    
    try {
      const response = (await searchProjectImplementedByProjectId(projectId, filter)) as IApiResponse<IProjectImplementedsResponse>;
      setObj(response.result);
      setIsDetails(false);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
    }
  }

  const rangePeriod = (type: string) => {
    if(filterState.period === "Total" && obj && obj.projectImplementeds.length > 0){
      const date = dayjs().set('month', obj.projectImplementeds[type === "end" ? obj.projectImplementeds.length-1 : 0].month -1).set('year', obj.projectImplementeds[type === "end" ? obj.projectImplementeds.length-1 : 0].year);
      return type === "end" ? date.endOf('month') : date.startOf('month');
    } else{
      return dayjs();
    }
  }

  async function updatePage(month: number, year: number, isDetails: boolean) {
    let response;
    if(!isDetails){
      response = (await getProjectImplementedByProjectIdAndMonthYear(projectId, month, year)) as IApiResponse<IProjectImplementedsResponse>;
      setIsDetails(true);
      setObj(response.result);
    } else {
      loadInfo()
    }
  }

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      console.log(params);

      try {
        if(obj){
          params.successCallback(
            obj.projectImplementeds,
            obj.projectImplementeds.length
          );
        }
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      }
    },
  });

  return (
    <div className={styles.implemented}>
      <div className={styles.container}>
        <h3 className={styles.title}>Alocado</h3>
        {
          !isDetails &&
          <>
            <DefaultFilterImplemented onFilterChange={handleFilterChange} 
              startDate={rangePeriod("start")} 
              endDate={rangePeriod("end")}
            />
          </>
        }
        {
          isDetails && 
          <Button
            className={styles.buttonMui}
            onClick={() => loadInfo()}
          >
            {vectorIcon && <img src={vectorIcon} />}
            Voltar a visão geral
          </Button>
        }
      </div>

      <div className={styles.infoContainer}>
        <CardInfo text="Horas Contratadas" value={obj ? obj.budgetHoursTotal : 0} type="hour" icon="AccessTimeIcon" />
        <CardInfo text="Horas Planejadas" value={obj ? obj.plannedHoursTotal : 0} type="hour" icon="AccessTimeIcon" />
        <CardInfo text="Horas Realizadas" value={obj ? obj.implementedHoursTotal : 0} type="hour" icon="AccessTimeIcon" />
        <CardInfo text="Custo Contratado" value={obj ? obj.budgetPriceTotal : 0} type="money" icon="MoneyOffIcon" />
        <CardInfo text="Custo Planejado" value={obj ? obj.plannedPriceTotal : 0}  type="money" icon="AttachMoneyIcon" />
        <CardInfo text="Custo Alocado" value={obj ? obj.implementedPriceTotal : 0}  type="money" icon="ShowChartIcon" />
      </div>
      {
        !isDetails && obj && obj.projectImplementeds.length > 0 && 
        <>
          <div className={styles.graphInfo}>
            <h4>{graphType === 0 ? "Custo" : "Hora"} X Mês</h4>
            <div className={styles.divButtons}>
              <button onClick={() => setGraphType(0)} className={ graphType == 0 ? styles.activeButton : styles.nonActiveButton} title="Custo X Mês"><AttachMoneyIcon width="8" height="8" /></button>
              <button onClick={() => setGraphType(1)} className={ graphType == 1 ? styles.activeButton : styles.nonActiveButton} title="Hora X Mês"><AccessTimeIcon width="8" height="8" /></button>
            </div>
          </div>
          {
            graphType === 0
              ? <LineChartImplemented projectImplemented={obj.projectImplementeds} graphType={graphType} />
              : <BarChartImplemented projectImplemented={obj.projectImplementeds} graphType={graphType} />
          }
        </>
      }
      {
        isDetails && obj && obj.projectImplementeds.length > 0 && 
        <>
          <div className={styles.graphInfo}>
            <h4>{graphType === 0 ? "Custo" : "Horas"} X Mês</h4>
            <div className={styles.divButtons}>
              <button onClick={() => setGraphType(0)} className={ graphType == 0 ? styles.activeButton : styles.nonActiveButton} title="Custo X Mês"><AttachMoneyIcon width="8" height="8" /></button>
              <button onClick={() => setGraphType(1)} className={ graphType == 1 ? styles.activeButton : styles.nonActiveButton} title="Hora X Mês"><AccessTimeIcon width="8" height="8" /></button>
            </div>
          </div>
          <BarChartImplemented projectImplemented={obj.projectImplementeds} graphType={graphType} />
        </> 
      }
      <div className={styles.dataGridContainer}>
        <AgGridReact
            ref={gridRef}
            theme={themeGrid}
            localeText={localeText}
            loading={loading}
            columnDefs={columns}
            overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
            defaultColDef={{
              sortable: true,
              editable: false,
              filter: true,
              floatingFilter: true,
            }}
            suppressCellFocus={true}
            rowStyle={{ cursor: "pointer" }}
            onRowClicked={(row) => updatePage(row.data.month, row.data.year, isDetails)}
            cacheBlockSize={50}
            rowModelType="infinite"
            datasource={getDataSource()}
            animateRows={true}
          />
      </div>

    </div>
  );
};
